import React, { forwardRef, RefForwardingComponent, useMemo } from 'react';

import ellipsis from 'polished/lib/mixins/ellipsis';

import BaseInput, { InputProps } from '@gojob/ui-bits/Input';
import useTheme from '@gojob/ui-bits/styles/useTheme';

const useStyles = () => {
  const { colors, spacing, fonts } = useTheme();
  return useMemo(
    () =>
      ({
        root: {
          border: 'none',
          borderRadius: 0,
          borderBottom: `1px solid ${colors.inactive}`,
          padding: 0,
          minHeight: spacing(4),
          backgroundColor: colors.transparent,
          ':hover': {
            boxShadow: 'unset',
            border: 'none',
            borderRadius: 0,
            borderBottom: `1px solid ${colors.primary}`,
          },
          ':focus-within': {
            boxShadow: 'unset',
            border: 'none',
            borderRadius: 0,
            borderBottom: `1px solid ${colors.primary}`,
          },
          '& input': {
            ...fonts.headline3,
            lineHeight: 1,
            color: colors.bayOfMary,
            ...ellipsis('100%'),
            '::placeholder': {
              ...fonts.messageMedium,
              fontWeight: 900,
              lineHeight: 1,
              color: colors.inactive,
            },
          },
        },
        error: {
          border: 'unset',
          borderBottom: `1px solid ${colors.alert}`,
        },
      } as const),
    [colors, spacing, fonts]
  );
};

const Input: RefForwardingComponent<HTMLInputElement, InputProps<'input'>> = (props, ref) => {
  const { error, ...other } = props;
  const classes = useStyles();

  return <BaseInput css={[classes.root, error && classes.error]} ref={ref} {...other} />;
};

export default forwardRef(Input);
